import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { apiClient } from "../../services/api";
import { webhookKeyService } from "../../services/webhookKeyService";
import { DryRunResponse, RuleType, WebhookKey } from "../../types";

interface Props {
  open: boolean;
  onClose: () => void;
  ruleId: string;
  ruleType: RuleType;
}

const DEFAULT_PAYLOAD_TEMPLATE = JSON.stringify(
  {
    cellulare: "+393331234567",
    email: "test@example.com",
    nome: "Mario",
    cognome: "Rossi",
  },
  null,
  2
);

export default function DryRunDialog({ open, onClose, ruleId, ruleType }: Props) {
  const [keys, setKeys] = useState<WebhookKey[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [keysError, setKeysError] = useState<string | null>(null);

  const [selectedKeyId, setSelectedKeyId] = useState<string>("");
  const [apiKeyPlain, setApiKeyPlain] = useState<string>("");
  const [leadJson, setLeadJson] = useState<string>(DEFAULT_PAYLOAD_TEMPLATE);
  const [leadIdField, setLeadIdField] = useState<string>("");

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [result, setResult] = useState<DryRunResponse | null>(null);

  // Carica le keys con scope "lead-dispatch" all'apertura.
  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoadingKeys(true);
      setKeysError(null);
      try {
        const res = await webhookKeyService.list({ status: "active" });
        const all: WebhookKey[] = res.data?.data?.keys ?? [];
        const eligible = all.filter((k) =>
          (k.scopes ?? []).includes("lead-dispatch")
        );
        setKeys(eligible);
      } catch (e) {
        console.error(e);
        setKeysError(
          "Impossibile caricare le webhook keys. Crea una key con scope lead-dispatch da /egg/webhook-keys."
        );
      } finally {
        setLoadingKeys(false);
      }
    };
    load();
  }, [open]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setResult(null);
      setRunError(null);
      setApiKeyPlain("");
      setSelectedKeyId("");
      setLeadIdField("");
      setLeadJson(DEFAULT_PAYLOAD_TEMPLATE);
    }
  }, [open]);

  const canRun = useMemo(() => {
    if (running) return false;
    if (!apiKeyPlain.trim()) return false;
    try {
      JSON.parse(leadJson);
      return true;
    } catch {
      return false;
    }
  }, [apiKeyPlain, leadJson, running]);

  const handleRun = async () => {
    setRunning(true);
    setRunError(null);
    setResult(null);
    try {
      const data = JSON.parse(leadJson);
      const body: Record<string, unknown> = {
        key: apiKeyPlain.trim(),
        ruleId,
        data,
      };
      if (leadIdField.trim()) body.leadId = leadIdField.trim();
      const res = await apiClient.post<DryRunResponse>(
        "/v1/lead-dispatch/leads?dryRun=true",
        body
      );
      setResult(res.data);
    } catch (e) {
      console.error(e);
      setRunError("Errore durante il dry-run. Verifica payload e chiave.");
    } finally {
      setRunning(false);
    }
  };

  const handleCopyPreview = () => {
    if (result && result.status === "dry-run") {
      navigator.clipboard.writeText(
        JSON.stringify(result.outbound_preview, null, 2)
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Test dispatch (dry-run){" "}
        <Chip
          label={ruleType}
          size="small"
          color={ruleType === "simple" ? "primary" : "secondary"}
          variant="outlined"
          sx={{ ml: 1 }}
        />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Simula un dispatch senza chiamare il partner né scrivere log. Per i
          dispatcher, mostra anche quale simple rule sarebbe selezionata
          dall'attuale schedule + weighted pick.
        </Typography>

        {keysError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {keysError}
          </Alert>
        )}

        <Stack spacing={2}>
          <FormControl size="small" fullWidth>
            <InputLabel id="dry-run-key-select">Webhook key (scope lead-dispatch)</InputLabel>
            <Select
              labelId="dry-run-key-select"
              label="Webhook key (scope lead-dispatch)"
              value={selectedKeyId}
              onChange={(e) => {
                setSelectedKeyId(e.target.value);
                // L'apiKey plaintext non è recuperabile dal backend — l'admin lo incolla a mano sotto.
                // Questa select serve solo a ricordare quale partner sto usando.
              }}
              disabled={loadingKeys || keys.length === 0}
            >
              {loadingKeys && (
                <MenuItem value="" disabled>
                  <CircularProgress size={14} sx={{ mr: 1 }} />
                  Caricamento…
                </MenuItem>
              )}
              {!loadingKeys && keys.length === 0 && (
                <MenuItem value="" disabled>
                  Nessuna key con scope lead-dispatch
                </MenuItem>
              )}
              {keys.map((k) => (
                <MenuItem key={k.id} value={k.id}>
                  {k.partnerName} —{" "}
                  <Typography
                    variant="caption"
                    fontFamily="monospace"
                    sx={{ ml: 0.5 }}
                  >
                    {k.keyPrefix}…
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="API key (plaintext)"
            size="small"
            fullWidth
            value={apiKeyPlain}
            onChange={(e) => setApiKeyPlain(e.target.value)}
            type="password"
            autoComplete="off"
            placeholder="egg_xxxxxxxxxxxxxxxxxxxxxxxxxxx"
            helperText="Incolla qui il valore plaintext della key — non è memorizzato lato server e non è recuperabile."
          />

          <TextField
            label="Lead ID (opzionale)"
            size="small"
            fullWidth
            value={leadIdField}
            onChange={(e) => setLeadIdField(e.target.value)}
            helperText="Usato come chiave di idempotency. In dry-run NON viene scritto."
          />

          <TextField
            label="Payload data (JSON)"
            multiline
            minRows={6}
            maxRows={16}
            fullWidth
            value={leadJson}
            onChange={(e) => setLeadJson(e.target.value)}
            inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
          />

          {runError && <Alert severity="error">{runError}</Alert>}

          {result && result.status === "skipped" && (
            <Alert severity="warning">
              <strong>Dispatch saltato.</strong> Reason:{" "}
              <code>{result.reason}</code>
            </Alert>
          )}

          {result && result.status === "dry-run" && (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Anteprima outbound
                </Typography>
                <Chip
                  label={`→ ${result.outbound_preview.selected_simple_rule_id}`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontFamily: "monospace" }}
                />
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Copia JSON">
                  <IconButton size="small" onClick={handleCopyPreview}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: "grey.100",
                  fontSize: 12,
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  maxHeight: 360,
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: 1,
                }}
              >
                {JSON.stringify(result.outbound_preview, null, 2)}
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running}>
          Chiudi
        </Button>
        <Button
          variant="contained"
          onClick={handleRun}
          disabled={!canRun}
        >
          {running ? "In esecuzione…" : "Esegui dry-run"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
